import {
  PREPARING_COPY as C,
  PREPARING_STEP_COPY,
  PREPARING_FALLBACK_STEPS,
} from "./copy";
import arisOrb from "../../Assets/icon/capture/aris-orb.png";
import stepDoneIcon from "../../Assets/icon/explore/preparing/step-done.svg";
import stepActiveIcon from "../../Assets/icon/explore/preparing/step-active.svg";
import clockIcon from "../../Assets/icon/explore/preparing/clock.svg";
import bellIcon from "../../Assets/icon/explore/preparing/bell.svg";
import heartIcon from "../../Assets/icon/explore/preparing/heart.svg";
import ringOuter from "../../Assets/icon/explore/preparing/hero-ring-1.svg";
import ringInner from "../../Assets/icon/explore/preparing/hero-ring-2.svg";
import ringWide from "../../Assets/icon/explore/preparing/hero-ring-3.svg";
import voiceIcon from "../../Assets/icon/explore/sheet-voice.svg";

/**
 * Explore Preparing page — Figma 9862:24379.
 *
 * Shown while `shouldShowPreparing` holds (see Services/explore): the feed has
 * no sections, or `/explore/preparation` is still PREPARING inside its run
 * window. The parent polls the checklist and hands it down as `prep`.
 *
 * - Steps render as sent, in `order`; `tracked: false` is omitted. The
 *   design's Current Focus and Guided Journeys steps are retired (WP-W12), so
 *   they only appear if the server sends them.
 * - COMPLETE ticks, IN_PROGRESS spins, anything else (PENDING, FAILED) waits.
 * - "Ready within N hours" comes from `estimated_ready_by`; there is no
 *   "Aris will notify you" line because aris-ai sends no READY notification.
 */

const STEP_STATUS = { COMPLETE: "done", IN_PROGRESS: "active" };

const hoursUntil = (iso, now = Date.now()) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t) || t <= now) return null;
  return Math.max(1, Math.ceil((t - now) / 3600000));
};

function StepIcon({ status }) {
  // The exported check is the white tick only; the teal disc is the frame's
  // fill in Figma, so CSS draws it (as Capture's cap-check-disc does).
  if (status === "done") {
    return (
      <img
        className="exp-step__icon exp-step__icon--done"
        src={stepDoneIcon}
        alt=""
        width="22"
        height="22"
      />
    );
  }
  if (status === "active") {
    return (
      <img
        className="exp-step__icon exp-step__icon--spin"
        src={stepActiveIcon}
        alt=""
        width="22"
        height="22"
      />
    );
  }
  return <span className="exp-step__icon exp-step__icon--pending" />;
}

function PreparingView({ prep, onTalkWithAris }) {
  const steps = (Array.isArray(prep?.steps) ? prep.steps : PREPARING_FALLBACK_STEPS)
    .filter((step) => step?.tracked !== false)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const hours = hoursUntil(prep?.estimated_ready_by);

  return (
    <>
      <section className="exp-hero">
        <span className="exp-hero__rings" aria-hidden="true">
          <img className="exp-hero__ring exp-hero__ring--outer" src={ringOuter} alt="" width="333" height="333" />
          <img className="exp-hero__ring exp-hero__ring--inner" src={ringInner} alt="" width="85" height="85" />
          <img className="exp-hero__ring exp-hero__ring--wide" src={ringWide} alt="" width="141" height="141" />
        </span>
        <div className="exp-hero__eyebrow">
          <img src={arisOrb} alt="" width="42" height="42" />
          {C.eyebrow}
        </div>
        <h2 className="exp-hero__title">{C.title}</h2>
        {C.body.map((paragraph) => (
          <p key={paragraph} className="exp-hero__body">
            {paragraph}
          </p>
        ))}
      </section>

      <section className="exp-steps" aria-live="polite">
        {steps.map((step) => {
          const status = STEP_STATUS[step.state] || "pending";
          // The server's resolved strings (gaps E7) win; the keyed copy is the
          // fallback for older servers. With neither, skip the step rather
          // than print a key.
          const title = step.title || PREPARING_STEP_COPY[step.title_key];
          if (!title) return null;
          const subtitle = step.subtitle || PREPARING_STEP_COPY[step.subtitle_key];
          return (
            <div key={step.key} className={`exp-step exp-step--${status}`}>
              <StepIcon status={status} />
              <span className="exp-step__text">
                <span className="exp-step__title">{title}</span>
                {subtitle && <span className="exp-step__subtitle">{subtitle}</span>}
              </span>
            </div>
          );
        })}
      </section>

      <section className="exp-why">
        <h3 className="exp-why__title">
          <img src={clockIcon} alt="" width="16" height="16" />
          {C.whyTitle}
        </h3>
        <p className="exp-why__body">{C.whyBody}</p>
      </section>

      <section className="exp-wait">
        <h3 className="exp-wait__title">{C.waitTitle}</h3>
        <p className="exp-wait__body">{C.waitBody}</p>
        <button type="button" className="exp-wait__btn" onClick={onTalkWithAris}>
          <img src={voiceIcon} alt="" width="24" height="24" />
          {C.waitCta}
        </button>
      </section>

      {hours && (
        <section className="exp-notice">
          <img src={bellIcon} alt="" width="16" height="16" />
          <p>
            {C.noticeLead}
            <strong>{C.noticeWithin(hours)}</strong>
          </p>
        </section>
      )}

      <p className="exp-footnote">
        <img src={heartIcon} alt="" width="16" height="16" />
        {C.footnote}
      </p>
    </>
  );
}

export default PreparingView;
